import React, { useState } from "react";
import { Link } from "react-router-dom";
import { SignedIn, SignedOut, SignInButton } from "@clerk/clerk-react";
import { ArrowRight, Check, ChevronDown } from "./Icons";
import { Footer } from "./Footer";
import { Header } from "./Header";

const tiers = [
  {
    name: "Free",
    price: "£0",
    description: "Perfect for individuals and small projects",
    features: [
      "1 personal workspace",
      "5 taskplans",
      "10 runs per month",
      "7-day run history",
    ],
    cta: "Get Started",
    highlighted: false,
  },
  {
    name: "Team",
    price: "£25",
    period: "/month",
    description: "For growing teams that need more power",
    features: [
      "Up to 30 members",
      "Unlimited teams",
      "Unlimited taskplans",
      "75 runs per month",
      "90-day run history",
      "GitHub Actions integration",
      "Role-based permissions",
    ],
    cta: "Try for free",
    highlighted: true,
  },
  {
    name: "Enterprise",
    price: "£150",
    period: "/month",
    description: "For organisations with advanced needs",
    features: [
      "Everything in Team, plus:",
      "Up to 100 members",
      "+£5 per additional member",
      "Unlimited runs",
      "Unlimited run history",
      "Priority email support",
      "Audit logging",
    ],
    cta: "Get started",
    highlighted: false,
  },
];

const faqs = [
  {
    question: "How does the free trial work?",
    answer:
      "The Team plan comes with a 14-day free trial. No credit card required. You get full access to all Team features during the trial period.",
  },
  {
    question: "Can I change plans later?",
    answer:
      "Yes! You can upgrade or downgrade your plan at any time. When upgrading, you'll get immediate access to new features. When downgrading, changes take effect at the end of your billing cycle.",
  },
  {
    question: "What payment methods do you accept?",
    answer:
      "We accept all major credit cards (Visa, Mastercard, American Express) through our secure payment partner Stripe. Enterprise customers can also pay via invoice.",
  },
  {
    question: "What counts as a 'run'?",
    answer:
      "A run is a single execution of a taskplan, whether it's triggered manually or scheduled. Each run tracks all task completions, timing data, and execution history.",
  },
];

export default function Pricing() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 transition-colors duration-200">
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-16 sm:pt-40 sm:pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 dark:text-white tracking-tight">
            Simple, transparent pricing
          </h1>
          <p className="mt-4 text-lg text-slate-500 dark:text-slate-400 max-w-2xl mx-auto">
            Start free, scale as you grow. No hidden fees, no surprises.
          </p>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 lg:gap-6">
            {tiers.map((tier) => (
              <div
                key={tier.name}
                className={`relative rounded-2xl p-8 flex flex-col ${
                  tier.highlighted
                    ? "bg-indigo-600 text-white ring-4 ring-indigo-600 ring-offset-4 ring-offset-white dark:ring-offset-slate-950 shadow-2xl scale-105"
                    : "bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-lg"
                }`}
              >
                {tier.highlighted && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-amber-400 to-orange-500 text-slate-900 text-xs font-bold uppercase tracking-wider px-4 py-1 rounded-full shadow-lg">
                    Most Popular
                  </div>
                )}
                <h3
                  className={`text-lg font-bold ${tier.highlighted ? "text-white" : "text-slate-900 dark:text-white"}`}
                >
                  {tier.name}
                </h3>
                <div className="mt-4 flex items-baseline gap-1">
                  <span
                    className={`text-4xl font-extrabold ${tier.highlighted ? "text-white" : "text-slate-900 dark:text-white"}`}
                  >
                    {tier.price}
                  </span>
                  {tier.period && (
                    <span
                      className={`text-sm ${tier.highlighted ? "text-indigo-200" : "text-slate-500 dark:text-slate-400"}`}
                    >
                      {tier.period}
                    </span>
                  )}
                </div>
                <p
                  className={`mt-2 text-sm ${tier.highlighted ? "text-indigo-200" : "text-slate-500 dark:text-slate-400"}`}
                >
                  {tier.description}
                </p>
                <ul className="mt-6 space-y-3 flex-1">
                  {tier.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-3">
                      <Check
                        className={`h-5 w-5 flex-shrink-0 ${tier.highlighted ? "text-indigo-200" : "text-green-500"}`}
                      />
                      <span
                        className={`text-sm ${tier.highlighted ? "text-white" : "text-slate-600 dark:text-slate-300"}`}
                      >
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>
                <SignedOut>
                  <SignInButton
                    mode="modal"
                    forceRedirectUrl="/workspace/billing"
                  >
                    <button
                      className={`mt-8 w-full py-3 px-4 rounded-lg font-semibold text-sm transition-all flex items-center justify-center gap-2 ${
                        tier.highlighted
                          ? "bg-white text-indigo-600 hover:bg-indigo-50 shadow-lg"
                          : "bg-indigo-600 text-white hover:bg-indigo-700 shadow-md"
                      }`}
                    >
                      {tier.cta}
                      <ArrowRight className="h-4 w-4" />
                    </button>
                  </SignInButton>
                </SignedOut>
                <SignedIn>
                  <Link
                    to="/workspace/billing"
                    className={`mt-8 w-full py-3 px-4 rounded-lg font-semibold text-sm transition-all flex items-center justify-center gap-2 ${
                      tier.highlighted
                        ? "bg-white text-indigo-600 hover:bg-indigo-50 shadow-lg"
                        : "bg-indigo-600 text-white hover:bg-indigo-700 shadow-md"
                    }`}
                  >
                    Get started
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </SignedIn>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-slate-50 dark:bg-slate-900">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-900 dark:text-white text-center mb-12">
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between gap-4"
                >
                  <span className="font-medium text-slate-900 dark:text-white">
                    {faq.question}
                  </span>
                  <ChevronDown
                    className={`h-5 w-5 text-slate-400 transition-transform ${openFaq === index ? "rotate-180" : ""}`}
                  />
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-4">
                    <p className="text-slate-600 dark:text-slate-300 text-sm leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-slate-900 dark:text-white">
            Ready to automate your workflows?
          </h2>
          <p className="mt-4 text-lg text-slate-500 dark:text-slate-400">
            Start with the free plan and upgrade when you're ready.
          </p>
          <SignedOut>
            <SignInButton mode="modal" forceRedirectUrl="/workspace/billing">
              <button className="mt-8 inline-flex items-center px-8 py-4 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 shadow-lg hover:shadow-xl transition-all">
                Get started free
                <ArrowRight className="ml-2 h-5 w-5" />
              </button>
            </SignInButton>
          </SignedOut>
          <SignedIn>
            <Link
              to="/workspace/billing"
              className="mt-8 inline-flex items-center px-8 py-4 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 shadow-lg hover:shadow-xl transition-all"
            >
              Get started
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </SignedIn>
        </div>
      </section>

      <Footer />
    </div>
  );
}
